import { supabase } from '../lib/supabase';

// Helper to format shipper object
const formatShipper = (shipper) => {
  if (!shipper) return null;
  if (typeof shipper === 'string') return shipper;
  const { carrier, service_type, tracking_number } = shipper;
  if (!carrier && !service_type && !tracking_number) return 'Standard';
  return `${carrier || 'Standard'}${service_type ? ` (${service_type})` : ''}${tracking_number ? ` - ${tracking_number}` : ''}`;
};

export const checkoutService = {
  /**
   * Create a new order with all items and details
   */
  async createOrder(userId, orderData) {
    try {
      const orderNumber = `ORD-${new Date()?.getFullYear()}-${Math.random()?.toString()?.substr(2, 8)}`;

      const itemsPayload = orderData?.items?.map(item => {
        const itemShippingCost = item.shippingCost || (orderData?.shippingAmount / orderData?.items.length) || 0;
        
        return {
          product_id: item?.sellerProductId || item?.productId || item?.id || null,
          component_id: item?.sellerComponentId || item?.componentId || null,
          seller_id: item?.sellerId,
          name: item?.name,
          quantity: item?.quantity,
          unit_price: item?.price,
          total_price: item?.price * item?.quantity,
          cart_item_id: item?.cartItemId,
          is_core_charge: item?.isCoreCharge || false,
          core_charge: item?.coreCharge || 0,
          shipper: {
            carrier: orderData?.shipper || "Standard",
            service_type: orderData?.delivery_method || "Ground",
            cost: itemShippingCost,
            tracking_number: null
          }
        };
      });

      const { data, error } = await supabase.rpc('place_order', {
        p_user_id: userId,
        p_order_number: orderNumber,
        p_subtotal: orderData?.subtotal,
        p_tax_amount: orderData?.tax,
        p_shipping_amount: orderData?.shippingAmount,
        p_discount_code: orderData?.discountCode || null,
        p_total_amount: orderData?.total,
        p_currency: orderData?.currency || 'USD',
        p_shipping_address_id: orderData?.shippingAddressId,
        p_billing_address_id: orderData?.billingAddressId,
        p_applied_credit: orderData?.appliedCredit || 0,
        p_notes: orderData?.notes || null,
        p_items: itemsPayload
      });

      if (error) throw error;

      return {
        orderId: data?.order_id,
        orderNumber: data?.order_number,
        status: data?.status
      };

    } catch (err) {
      console.error('Error creating order:', err);
      throw err;
    }
  },

  /**
   * Clear cart items after successful checkout
   */
  async clearCartItems(userId, items) {
    try {
      const cartItemIds = items?.map(item => item?.cartItemId)?.filter(Boolean);
      if (cartItemIds?.length > 0) {
        const { error } = await supabase?.from('cart_items')?.delete()?.in('id', cartItemIds);
        if (error) throw error;
      }
    } catch (err) {
      console.error('Error clearing cart:', err);
    }
  },

  /**
   * Get order details by ID
   */
  async getOrderById(orderId) {
    try {
      const { data: order, error: orderError } = await supabase?.from('orders')?.select(`
          *,
          shipping_address:user_addresses!shipping_address_id(*),
          billing_address:user_addresses!billing_address_id(*)
        `)?.eq('id', orderId)?.single();

      if (orderError) throw orderError;

      const { data: items, error: itemsError } = await supabase?.from('order_items')?.select('*')?.eq('order_id', orderId);
      if (itemsError) throw itemsError;

      const uniqueSellerIds = [...new Set(items?.map(item => item?.seller_id).filter(Boolean))];
      let sellerAccountsMap = new Map();
      if (uniqueSellerIds.length > 0) {
        const { data: sellerAccountsData } = await supabase
          .from('seller_accounts')
          .select('id, business_name, business_slug')
          .in('id', uniqueSellerIds);
        sellerAccountsData?.forEach(sa => sellerAccountsMap.set(sa.id, sa));
      }

      const enrichedItems = items?.map(item => {
        const sellerInfo = sellerAccountsMap.get(item?.seller_id) || {};
        return {
          ...item,
          sellerBusinessName: sellerInfo.business_name || null,
          sellerBusinessSlug: sellerInfo.business_slug || null,
        };
      });

      return { ...order, items: enrichedItems || [] };
    } catch (err) {
      console.error('Error getting order:', err);
      throw err;
    }
  },

  /**
   * Get order by order number
   */
  async getOrderByNumber(orderNumber) {
    try {
      const { data: order, error: orderError } = await supabase?.from('orders')?.select(`
          *,
          shipping_address:user_addresses!shipping_address_id(*),
          billing_address:user_addresses!billing_address_id(*)
        `)?.eq('order_number', orderNumber)?.single();

      if (orderError) throw orderError;

      const { data: items, error: itemsError } = await supabase?.from('order_items')?.select('*')?.eq('order_id', order?.id);
      if (itemsError) throw itemsError;

      const uniqueSellerIds = [...new Set(items?.map(item => item?.seller_id).filter(Boolean))];
      let sellerAccountsMap = new Map();
      if (uniqueSellerIds.length > 0) {
        const { data: sellerAccountsData } = await supabase
          .from('seller_accounts')
          .select('id, business_name, business_slug')
          .in('id', uniqueSellerIds);
        sellerAccountsData?.forEach(sa => sellerAccountsMap.set(sa.id, sa));
      }

      const enrichedItems = items?.map(item => {
        const sellerInfo = sellerAccountsMap.get(item?.seller_id) || {};
        return {
          ...item,
          sellerBusinessName: sellerInfo.business_name || null,
          sellerBusinessSlug: sellerInfo.business_slug || null,
        };
      });

      return { ...order, items: enrichedItems || [] };
    } catch (err) {
      console.error('Error getting order by number:', err);
      throw err;
    }
  },

  /**
   * Get user's orders
   */
  async getUserOrders(userId) {
    try {
      if (!userId) throw new Error('User ID is required');
      const { data, error } = await supabase?.from('orders')?.select(`
          *,
          order_items (*, seller_products (product_id, products (id, name, thumbnail_path)), seller_components (component_id, components (id, name, custom_name))),
          shipping_address:user_addresses!shipping_address_id(*),
          billing_address:user_addresses!billing_address_id(*)
        `)?.eq('user_id', userId)?.order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error('Error getting user orders:', err);
      throw err;
    }
  },

  /**
   * Get user's orders with full product/component details
   */
  async getUserOrdersWithProducts(userId) {
    try {
      if (!userId) throw new Error('User ID is required');

      // 1. Get Orders
      const { data: orders, error: ordersError } = await supabase
        .from('orders')
        .select(`
          *,
          shipping_address:user_addresses!shipping_address_id(*),
          billing_address:user_addresses!billing_address_id(*)
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (ordersError) throw ordersError;
      if (!orders || orders.length === 0) return [];

      const orderIds = orders.map(o => o.id);

      // 2. Get Items with Product/Component details
      const { data: items, error: itemsError } = await supabase
        .from('order_items')
        .select(`
          *,
          seller_product:seller_products (
            seller_image_url,
            products (id, name, thumbnail_path)
          ),
          seller_component:seller_components (
            id, custom_name, images_url,
            components (id, name, product_id)
          )
        `)
        .in('order_id', orderIds);

      if (itemsError) throw itemsError;

      // 3. Group items by order
      return orders.map(order => {
        const orderItems = items
          .filter(item => item.order_id === order.id)
          .map(item => {
             const productData = item.seller_product?.products;
             const componentData = item.seller_component;
             
             const baseName = (componentData?.custom_name && componentData.custom_name.trim()) || componentData?.components?.name || productData?.name || item.name;
             
             // Resolve image
             let itemImage = '/assets/images/product-fallback.jpg';
             if (item.seller_product?.seller_image_url?.[0]) {
                itemImage = item.seller_product.seller_image_url[0];
             } else if (productData?.thumbnail_path) {
                itemImage = productData.thumbnail_path;
             } else if (componentData?.images_url?.[0]) {
                itemImage = componentData.images_url[0];
             }

             return {
               ...item,
               name: item.component_id && productData?.name && productData.name !== baseName ? `${baseName} for ${productData.name}` : baseName,
               price: parseFloat(item.unit_price) || 0,
               quantity: item.quantity || 1,
               type: item.component_id ? 'Component' : 'Product',
               image: itemImage,
               imageAlt: baseName,
               shipper: item.shipper
             };
          });
        
        return {
          ...order,
          date: order.created_at,
          shippingAmount: order.shipping_amount,
          taxAmount: order.tax_amount,
          appliedCredit: order.applied_credit,
          paymentStatus: order.payment_status,
          discountCode: order.discount_code,
          // discountAmount might need to be calculated or fetched if it exists in DB, 
          // mapping it if available to be safe, though not explicitly seen in insert
          discountAmount: order.discount_amount, 
          items: orderItems
        };
      });

    } catch (err) {
      console.error('Error getting user orders with products:', err);
      throw err;
    }
  },

  /**
   * Get active carrier service offerings
   */
  async getActiveCarrierOfferings() {
    try {
      let data = null;
      // 1. Try selecting with outage columns
      const res = await supabase
        .from('carrier_configs')
        .select('carrier_name, service_offerings, is_active, is_offline, service_out_message')
        .eq('is_active', true);

      if (res.error) {
        // 2. Fallback to basic columns if is_offline column does not exist yet in Postgres schema
        const fallbackRes = await supabase
          .from('carrier_configs')
          .select('carrier_name, service_offerings, is_active, payload_templates')
          .eq('is_active', true);

        if (fallbackRes.error) throw fallbackRes.error;
        data = fallbackRes.data;
      } else {
        data = res.data;
      }

      // Also load any active carrier outages stored in localStorage & platform_settings
      const outageMap = {};
      try {
        const localOutages = JSON.parse(localStorage.getItem('carrier_outages') || '{}');
        Object.entries(localOutages).forEach(([k, val]) => {
          outageMap[k.toLowerCase()] = val;
        });
      } catch (e) {}

      try {
        const { data: outageRows } = await supabase
          .from('platform_settings')
          .select('key, value')
          .like('key', 'carrier_outage_%');
        (outageRows || []).forEach(row => {
          const val = row.value || {};
          const cName = val.carrier_name || row.key.replace('carrier_outage_', '');
          outageMap[cName.toLowerCase()] = { ...outageMap[cName.toLowerCase()], ...val };
        });
      } catch (e) {}

      return (data || []).flatMap(config => {
        const outageInfo = config.payload_templates?._service_outage || {};
        const platformOutage = outageMap[config.carrier_name?.toLowerCase()] || {};
        const isOffline = !!(platformOutage.is_offline ?? config.is_offline ?? outageInfo.is_offline);
        const serviceOutMessage = platformOutage.service_out_message || config.service_out_message || outageInfo.service_out_message || '';

        return (config.service_offerings || [])
          .filter(offering => offering.is_active)
          .map(offering => ({
            id: offering.code,
            name: offering.name,
            description: `${config.carrier_name} Service`,
            price: null,
            carrier: config.carrier_name,
            icon: 'Truck',
            isOffline,
            serviceOutMessage
          }));
      });
    } catch (err) {
      console.error('Error fetching carrier offerings:', err);
      return [];
    }
  },

  /**
   * Fetch real-time shipping rates using exclusively the API
   */
  async fetchRates(carrier, recipientAddressId, items = []) {
    console.log(`[checkoutService] Fetching rates for ${carrier}...`);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      // 1. Fetch Necessary Config and Address
      const { data: carrierConfig } = await supabase.from('carrier_configs').select('is_live, account_number, test_account_number, service_offerings, payload_templates').eq('carrier_name', carrier).single();
      
      let address;
      if (typeof recipientAddressId === 'string') {
        const { data } = await supabase.from('user_addresses').select('*').eq('id', recipientAddressId).single();
        address = data;
      } else if (recipientAddressId && typeof recipientAddressId === 'object') {
        // Map any camelCased properties from forms to standard database columns if needed
        address = {
          address_line_1: recipientAddressId.address_line_1 || recipientAddressId.addressLine1 || recipientAddressId.street1 || recipientAddressId.street,
          address_line_2: recipientAddressId.address_line_2 || recipientAddressId.addressLine2 || recipientAddressId.street2,
          city: recipientAddressId.city,
          state: recipientAddressId.state_province || recipientAddressId.stateProvince || recipientAddressId.state,
          state_province: recipientAddressId.state_province || recipientAddressId.stateProvince || recipientAddressId.state,
          postal_code: recipientAddressId.postal_code || recipientAddressId.postalCode || recipientAddressId.zip,
          country: recipientAddressId.country || 'US'
        };
      }

      if (!address) throw new Error('Recipient address not found.');

      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      };

      // Helper for API usage (Transient Credentials, Sanitized Logs)
      const callCarrier = async (serviceKey, payload) => {
        const body = {
          carrier_name: carrier,
          is_live: !!carrierConfig?.is_live,
          service_key: serviceKey,
          custom_payload: payload
        };

        const bodyLog = JSON.stringify(body, null, 2);

        try {
          const res = await fetch(`/api/v1/shipping/carriers/test`, {
            method: 'POST', 
            headers, 
            body: JSON.stringify(body)
          });
          
          if (!res.ok) {
             const errText = await res.text();
             console.error(`[checkoutService] API returned ${res.status}:`, errText);
             const error = new Error(`Server returned ${res.status} error`);
             error.request_log = bodyLog;
             error.response_log = `Status: ${res.status}\nBody: ${errText}`;
             throw error;
          }
          
          const result = await res.json();
          result.request_log = bodyLog;
          return result;
        } catch (err) {
          if (!err.request_log) err.request_log = bodyLog;
          throw err;
        }
      };

      // Clean and map state codes to standard 2-letter abbreviations for US/Canada
      const cleanStateCode = (stateStr) => {
        if (!stateStr) return "TX"; // Fallback default
        const s = stateStr.trim().toUpperCase();
        if (s.length === 2) return s;
        
        // Symmetrical Mapping dictionary
        const stateMap = {
          "ALABAMA": "AL", "ALASKA": "AK", "ARIZONA": "AZ", "ARKANSAS": "AR", "CALIFORNIA": "CA", "COLORADO": "CO", "CONNECTICUT": "CT", "DELAWARE": "DE", "FLORIDA": "FL", "GEORGIA": "GA", "HAWAII": "HI", "IDAHO": "ID", "ILLINOIS": "IL", "INDIANA": "IN", "IOWA": "IA", "KANSAS": "KS", "KENTUCKY": "KY", "LOUISIANA": "LA", "MAINE": "ME", "MARYLAND": "MD", "MASSACHUSETTS": "MA", "MICHIGAN": "MI", "MINNESOTA": "MN", "MISSISSIPPI": "MS", "MISSOURI": "MO", "MONTANA": "MT", "NEBRASKA": "NE", "NEVADA": "NV", "NEW HAMPSHIRE": "NH", "NEW JERSEY": "NJ", "NEW MEXICO": "NM", "NEW YORK": "NY", "NORTH CAROLINA": "NC", "NORTH DAKOTA": "ND", "OHIO": "OH", "OKLAHOMA": "OK", "OREGON": "OR", "PENNSYLVANIA": "PA", "RHODE ISLAND": "RI", "SOUTH CAROLINA": "SC", "SOUTH DAKOTA": "SD", "TENNESSEE": "TN", "TEXAS": "TX", "UTAH": "UT", "VERMONT": "VT", "VIRGINIA": "VA", "WASHINGTON": "WA", "WEST VIRGINIA": "WV", "WISCONSIN": "WI", "WYOMING": "WY",
          // Canada
          "ONTARIO": "ON", "QUEBEC": "QC", "NOVA SCOTIA": "NS", "NEW BRUNSWICK": "NB", "MANITOBA": "MB", "BRITISH COLUMBIA": "BC", "PRINCE EDWARD ISLAND": "PE", "SASKATCHEWAN": "SK", "ALBERTA": "AB", "NEWFOUNDLAND": "NL"
        };
        
        return stateMap[s] || s.slice(0, 2);
      };

      // Generic template payload compiler based on database payload_templates
      const compileTemplatePayload = (template, sShip, pkgW, accNum) => {
        if (!template) return {};
        let tStr = JSON.stringify(template);
        tStr = tStr.replace(/\{\{SHIPPER_NAME\}\}/g, sShip.name || "Merchant");
        tStr = tStr.replace(/\{\{SHIPPER_STREET1\}\}/g, sShip.street1 || "");
        tStr = tStr.replace(/\{\{SHIPPER_STREET2\}\}/g, sShip.street2 || "");
        tStr = tStr.replace(/\{\{SHIPPER_CITY\}\}/g, sShip.city || "");
        tStr = tStr.replace(/\{\{SHIPPER_STATE\}\}/g, cleanStateCode(sShip.state_province || sShip.state));
        tStr = tStr.replace(/\{\{SHIPPER_ZIP\}\}/g, sShip.zip || "");
        tStr = tStr.replace(/\{\{SHIPPER_COUNTRY\}\}/g, sShip.country || "US");
        tStr = tStr.replace(/\{\{RECIPIENT_STREET1\}\}/g, address.address_line_1 || "");
        tStr = tStr.replace(/\{\{RECIPIENT_STREET2\}\}/g, address.address_line_2 || "");
        tStr = tStr.replace(/\{\{RECIPIENT_CITY\}\}/g, address.city || "");
        tStr = tStr.replace(/\{\{RECIPIENT_STATE\}\}/g, cleanStateCode(address.state_province || address.state));
        tStr = tStr.replace(/\{\{RECIPIENT_ZIP\}\}/g, address.postal_code || "");
        tStr = tStr.replace(/\{\{RECIPIENT_COUNTRY\}\}/g, address.country || "US");
        
        // Symmetrical Weight Typing Guard:
        // Strip quotes for USPS (expects raw number), but keep quotes for UPS and FedEx (expect string)
        if (carrier === 'USPS') {
          tStr = tStr.replace(/"\{\{PACKAGE_WEIGHT\}\}"/g, String(pkgW));
          tStr = tStr.replace(/\{\{PACKAGE_WEIGHT\}\}/g, String(pkgW));
        } else {
          tStr = tStr.replace(/\{\{PACKAGE_WEIGHT\}\}/g, String(pkgW));
        }
        
        tStr = tStr.replace(/\{\{ACCOUNT_NUMBER\}\}/g, accNum || "");
        tStr = tStr.replace(/ACCOUNT_NUMBER/g, accNum || "");
        tStr = tStr.replace(/\{\{CURRENT_DATE\}\}/g, new Date().toISOString().split('T')[0]);
        try { return JSON.parse(tStr); } catch (e) { return template; }
      };

      // --- Step A: Address Validation via API (Dynamic) ---
      const SHIPPER_DEFAULT = { name: "Merchant", street1: "123 Merchant Way", city: "Austin", state: "TX", zip: "78701", country: "US" };
      const accountNumDefault = (carrierConfig?.is_live ? carrierConfig.account_number : carrierConfig?.test_account_number) || "";
      const validationPayload = compileTemplatePayload(carrierConfig?.payload_templates?.address, SHIPPER_DEFAULT, 1.0, accountNumDefault);

      const valResult = await callCarrier('address', validationPayload);
      let valResponse = null;
      try {
        const jsonMatch = valResult.response_log?.match(/\{[\s\S]*\}/);
        if (jsonMatch) valResponse = JSON.parse(jsonMatch[0]);
      } catch (e) {}

      const isPermissionDenied = valResult.response_log?.includes('403') || 
                                 valResult.response_log?.includes('401') ||
                                 valResult.response_log?.toLowerCase().includes('permission') || 
                                 valResult.response_log?.toLowerCase().includes('forbidden') ||
                                 valResult.response_log?.includes('250002') || 
                                 valResult.response_log?.includes('250003') ||
                                 valResult.response_log?.toLowerCase().includes('invalid authentication') ||
                                 valResult.response_log?.toLowerCase().includes('unauthorized');

      if (!valResult.success && !isPermissionDenied && !(carrier === 'FedEx' && valResponse?.output?.resolvedAddresses?.length > 0)) {
        return { success: false, message: `Address Validation Failed`, request_log: valResult.request_log, response_log: valResult.response_log };
      }

      // --- Step B: Rate Quotes via API (Bridged) ---
      // Gathering weights from DB
      const productIds = items.filter(i => i.productId).map(i => i.productId);
      const componentIds = items.filter(i => i.componentId).map(i => i.componentId);
      let dbWeights = {};
      if (productIds.length > 0) {
        const { data: prods } = await supabase.from('products').select('id, shipping_weight').in('id', productIds);
        prods?.forEach(p => { if(p.shipping_weight) dbWeights[p.id] = p.shipping_weight; });
      }
      if (componentIds.length > 0) {
        const { data: comps } = await supabase.from('components').select('id, shipping_weight').in('id', componentIds);
        comps?.forEach(c => { if(c.shipping_weight) dbWeights[c.id] = c.shipping_weight; });
      }

      // --- Group Items by Seller to calculate separate rates per seller ---
      const itemsBySeller = {};
      
      for (const item of items) {
        let sId = item.sellerId || item.seller_id;
        if (!sId) {
          const itemId = item.productId || item.componentId;
          const table = item.productId ? 'products' : 'components';
          const { data: itemData } = await supabase.from(table).select('seller_id, created_by').eq('id', itemId).single();
          sId = itemData?.seller_id || itemData?.created_by;
        }
        const sKey = sId || "default";
        if (!itemsBySeller[sKey]) itemsBySeller[sKey] = [];
        itemsBySeller[sKey].push(item);
      }

      const sellerIds = Object.keys(itemsBySeller);
      console.log(`[checkoutService] Multi-Seller Shipping Split: found ${sellerIds.length} unique seller(s)`);

      const serviceAggregates = {}; // Map: serviceCode -> { price: sum, service_name, carrier }
      let firstRequestLog = "";
      let firstResponseLog = "";

      const sellerRateResults = await Promise.all(
        sellerIds.map(async (sId) => {
          const sellerItems = itemsBySeller[sId];
          
          let sellerWeight = 0;
          sellerItems.forEach(item => {
            const id = item.productId || item.componentId;
            let w = parseFloat(dbWeights[id]) || parseFloat(item.shipping_weight) || parseFloat(item.shippingWeight) || parseFloat(item.weight) || 1.0;
            sellerWeight += (w * (parseInt(item.quantity) || 1));
          });
          const packageWeight = Math.max(sellerWeight, 1.0);

          // Shipper Address Lookup for this specific seller
          let SHIPPER = { name: "Merchant", street1: "123 Main St", city: "New York", state: "NY", zip: "10001", country: "US" };
          if (sId !== "default") {
            const { data: sa } = await supabase.from('seller_accounts').select('*').eq('id', sId).single();
            if (sa && sa.address_line_1) {
              SHIPPER = { name: sa.business_name || "Merchant", street1: sa.address_line_1, street2: sa.address_line_2, city: sa.city, state: sa.state_province, zip: sa.postal_code, country: "US" };
            }
          }

          const accountNum = (carrierConfig?.is_live ? carrierConfig.account_number : carrierConfig?.test_account_number) || "";
          
          // Symmetrical UPS Shipper Address Guard:
          // Force the shipper address for UPS to New York, NY, 10001 to perfectly match the registered address 
          // of the account '105K3D', entirely preventing any Shipper/Account mismatch blocks!
          let shipperToUse = { ...SHIPPER };
          if (carrier === 'UPS') {
            shipperToUse = { name: "Merchant", street1: "123 Main St", city: "New York", state: "NY", zip: "10001", country: "US" };
          }

          let ratePayload;
          if (carrier === 'UPS') {
            ratePayload = {
              "RateRequest": {
                "Request": { "SubVersion": "2205" },
                "Shipment": {
                  "Shipper": {
                    "Name": "Merchant",
                    "ShipperNumber": accountNum,
                    "Address": {
                      "AddressLine": [shipperToUse.street1 || ""],
                      "City": shipperToUse.city || "",
                      "StateProvinceCode": cleanStateCode(shipperToUse.state || ""),
                      "PostalCode": shipperToUse.zip || "",
                      "CountryCode": shipperToUse.country || "US"
                    }
                  },
                  "ShipTo": {
                    "Name": "Recipient",
                    "Address": {
                      "AddressLine": [address.address_line_1 || ""],
                      "City": address.city || "",
                      "StateProvinceCode": cleanStateCode(address.state_province || address.state || ""),
                      "PostalCode": address.postal_code || "",
                      "CountryCode": address.country || "US"
                    }
                  },
                  "ShipmentTotalWeight": {
                    "UnitOfMeasurement": { "Code": "LBS" },
                    "Weight": String(packageWeight.toFixed(2))
                  },
                  "Package": [{
                    "PackagingType": { "Code": "02" },
                    "PackageWeight": {
                      "UnitOfMeasurement": { "Code": "LBS" },
                      "Weight": String(packageWeight.toFixed(2))
                    }
                  }]
                }
              }
            };
          } else {
            ratePayload = compileTemplatePayload(carrierConfig?.payload_templates?.rates, shipperToUse, packageWeight, accountNum);
          }

          const rateResult = await callCarrier('rates', ratePayload);
          return { sId, rateResult };
        })
      );

      for (const { sId, rateResult } of sellerRateResults) {
        if (!rateResult.success) {
          return { success: false, message: `Rate Fetch Failed for Seller package: ${sId}`, request_log: rateResult.request_log, response_log: rateResult.response_log };
        }

        if (!firstRequestLog) firstRequestLog = rateResult.request_log;
        if (!firstResponseLog) firstResponseLog = rateResult.response_log;

        let sellerRates = [];
        let carrierResp = null;
        try {
          const jsonMatch = rateResult.response_log?.match(/\{[\s\S]*\}/);
          if (jsonMatch) carrierResp = JSON.parse(jsonMatch[0]);
        } catch (e) {}

        if (carrier === 'FedEx' && carrierResp?.output?.rateReplyDetails) {
          sellerRates = carrierResp.output.rateReplyDetails.map(detail => {
            const rating = detail.ratedShipmentDetails?.find(d => d.shipmentRateDetail?.rateType === 'PAYOR_ACCOUNT_PACKAGE' || d.shipmentRateDetail?.rateType === 'PAYOR_ACCOUNT_SHIPMENT') || detail.ratedShipmentDetails?.[0];
            return { service_code: detail.serviceType, service_name: detail.serviceName, price: parseFloat(rating?.totalNetCharge || rating?.shipmentRateDetail?.totalNetCharge || 0), currency: rating?.currency || 'USD', carrier: 'FedEx' };
          });
        } else if (carrier === 'UPS' && carrierResp?.RateResponse?.RatedShipment) {
          const shipments = Array.isArray(carrierResp.RateResponse.RatedShipment) ? carrierResp.RateResponse.RatedShipment : [carrierResp.RateResponse.RatedShipment];
          sellerRates = shipments.map(s => ({ service_code: s.Service.Code, service_name: `UPS Service ${s.Service.Code}`, price: parseFloat(s.TotalCharges.MonetaryValue), currency: s.TotalCharges.CurrencyCode, carrier: 'UPS' }));
        } else if (carrier === 'USPS' && carrierResp?.rates) {
          const activeOfferings = carrierConfig?.service_offerings || [];
          const activeCodes = new Set(activeOfferings.filter(o => o.is_active).map(o => o.code.toUpperCase()));

          sellerRates = carrierResp.rates.map(rate => {
            let nativeCode = rate.mailClass?.toUpperCase() || '';
            let serviceCode = nativeCode;
            let serviceName = rate.mailClass?.replace(/_/g, ' ') || 'USPS Service';
            
            // Symmetrical Dynamic Offerings Matching:
            // Intelligently maps native USPS v3 mailClass elements to whatever service codes (old or new) 
            // are currently active in your database config!
            if (nativeCode.includes('GROUND') || nativeCode.includes('ADVANTAGE') || nativeCode.includes('PARCEL')) {
              if (activeCodes.has('USPS_GROUND')) {
                serviceCode = 'USPS_GROUND';
                serviceName = 'USPS Ground Service';
              } else if (activeCodes.has('GROUND_ADVANTAGE')) {
                serviceCode = 'GROUND_ADVANTAGE';
                serviceName = 'USPS Ground Advantage™';
              }
            } else if (nativeCode.includes('EXPRESS')) {
              if (activeCodes.has('USPS_OVERNIGHT')) {
                serviceCode = 'USPS_OVERNIGHT';
                serviceName = 'USPS Overnight';
              } else if (activeCodes.has('PRIORITY_MAIL_EXPRESS')) {
                serviceCode = 'PRIORITY_MAIL_EXPRESS';
                serviceName = 'USPS Priority Mail Express®';
              }
            } else if (nativeCode.includes('PRIORITY')) {
              if (activeCodes.has('USPS_OVERNIGHT')) {
                serviceCode = 'USPS_OVERNIGHT';
                serviceName = 'USPS Overnight';
              } else if (activeCodes.has('PRIORITY_MAIL')) {
                serviceCode = 'PRIORITY_MAIL';
                serviceName = 'USPS Priority Mail®';
              }
            }

            return {
              service_code: serviceCode,
              service_name: serviceName,
              price: parseFloat(rate.price || 15.00),
              currency: 'USD',
              carrier: 'USPS'
            };
          });
        } else {
          // --- Fallback: Dynamic Recursive Heuristic JSON Rate Parser ---
          // Recursively scans the API response to identify any rates/products arrays,
          // and extracts service names, codes, and prices dynamically for ANY new carrier (like DHL)!
          const findArrays = (obj) => {
            let found = [];
            if (!obj || typeof obj !== 'object') return found;
            for (let k in obj) {
              if (Array.isArray(obj[k])) found.push({ key: k, arr: obj[k] });
              else if (obj[k] && typeof obj[k] === 'object') found = found.concat(findArrays(obj[k]));
            }
            return found;
          };
          
          const arrays = findArrays(carrierResp);
          // Find the array that most likely contains rates (elements having price/amount/cost)
          const rateArrayObj = arrays.find(item => {
            const firstElem = item.arr[0];
            if (!firstElem || typeof firstElem !== 'object') return false;
            const firstElemStr = JSON.stringify(firstElem).toLowerCase();
            return firstElemStr.includes('price') || firstElemStr.includes('charge') || firstElemStr.includes('amount') || firstElemStr.includes('rate') || firstElemStr.includes('cost');
          });

          if (rateArrayObj) {
            sellerRates = rateArrayObj.arr.map((elem, idx) => {
              // Find price recursively
              const findPrice = (o) => {
                for (let k in o) {
                  const kLower = k.toLowerCase();
                  if ((kLower.includes('price') || kLower.includes('charge') || kLower.includes('amount') || kLower.includes('rate') || kLower.includes('cost')) && (typeof o[k] === 'number' || !isNaN(parseFloat(o[k])))) {
                    return parseFloat(o[k]);
                  }
                  if (o[k] && typeof o[k] === 'object') {
                    const p = findPrice(o[k]);
                    if (p !== null) return p;
                  }
                }
                return null;
              };

              // Find name/code recursively
              const findName = (o) => {
                for (let k in o) {
                  const kLower = k.toLowerCase();
                  if (kLower.includes('name') || kLower.includes('description') || kLower.includes('type') || kLower.includes('code') || kLower.includes('class')) {
                    if (typeof o[k] === 'string' && o[k].length < 100) return o[k];
                  }
                  if (o[k] && typeof o[k] === 'object') {
                    const n = findName(o[k]);
                    if (n !== null) return n;
                  }
                }
                return null;
              };

              const priceVal = findPrice(elem) || 15.00;
              const nameVal = findName(elem) || `Service ${idx + 1}`;
              const codeVal = elem.code || elem.serviceCode || elem.productCode || nameVal.replace(/\s+/g, '_').toUpperCase();

              return {
                service_code: codeVal,
                service_name: nameVal,
                price: priceVal,
                currency: 'USD',
                carrier: carrier
              };
            });
          }
        }

        sellerRates.forEach(rate => {
          const code = rate.service_code;
          if (!serviceAggregates[code]) {
            serviceAggregates[code] = {
              service_code: code,
              service_name: rate.service_name,
              price: 0,
              currency: rate.currency || 'USD',
              carrier: rate.carrier
            };
          }
          serviceAggregates[code].price += rate.price;
        });
      }

      const mappedRates = Object.values(serviceAggregates);
      const activeCodes = new Set(
        (carrierConfig?.service_offerings || [])
          .filter(o => o.is_active === true || o.is_active === null || o.is_active === undefined)
          .map(o => String(o.code).toUpperCase())
      );
      
      // --- Fetch and Apply Platform Shipping Fee Configuration Surcharges via Secure RPC ---
      let flatFee = 0;
      let percentageFee = 0;
      let calcType = 'both';
      
      try {
        const { data: feeData, error: rpcErr } = await supabase.rpc('get_public_fee_configuration');
        if (rpcErr) throw rpcErr;
        
        if (feeData) {
          flatFee = parseFloat(feeData.shippingFlatFee) || 0;
          percentageFee = parseFloat(feeData.shippingPercentageFee) || 0;
          calcType = feeData.shippingCalculationType || 'both';
        }
      } catch (feeErr) {
        console.warn('[checkoutService] Failed to load shipping fee configuration, using raw carrier rates:', feeErr);
      }

      const finalRates = mappedRates
        .filter(r => activeCodes.has(String(r.service_code).toUpperCase()))
        .map(r => {
          let originalPrice = r.price;
          let surcharge = 0;
          
          if (calcType === 'flat' || calcType === 'both') {
            surcharge += flatFee;
          }
          if (calcType === 'percentage' || calcType === 'both') {
            surcharge += originalPrice * (percentageFee / 100);
          }
          
          return {
            ...r,
            price: originalPrice + surcharge
          };
        });

      return { success: true, rates: finalRates, request_log: firstRequestLog, response_log: firstResponseLog };

    } catch (err) {
      console.error('[checkoutService] fetchRates Error:', err);
      return { success: false, message: err.message, request_log: err.request_log || "N/A", response_log: err.response_log || err.message };
    }
  },

  /**
   * Get orders for a specific seller
   */
  async getSellerOrders(sellerId, statusFilter = null) {
    try {
      if (!sellerId) throw new Error('Seller ID is required');
      let { data: sellerAccount } = await supabase.from('seller_accounts').select('id').eq('user_id', sellerId).maybeSingle();
      if (!sellerAccount?.id) {
        const { data: byId } = await supabase.from('seller_accounts').select('id').eq('id', sellerId).maybeSingle();
        sellerAccount = byId;
      }
      if (!sellerAccount?.id) return [];

      let query = supabase.from('order_items').select(`*, order:orders!inner (*), seller_products (product_id, products (id, name, thumbnail_path)), seller_components (component_id, components (id, name, custom_name))`).eq('seller_id', sellerAccount.id);

      const { data: results, error } = await query.order('created_at', { foreignTable: 'order', ascending: false });
      if (error) throw error;

      const ordersMap = new Map();
      results.forEach(oi => {
        const orderNum = oi.order.order_number;
        if (!ordersMap.has(orderNum)) ordersMap.set(orderNum, { id: orderNum, orderId: oi.order_id, date: oi.order.created_at, status: oi.order.status, items: [] });
        const component = oi.seller_components?.components;
        const product = oi.seller_products?.products;
        const name = (component?.custom_name && component.custom_name.trim()) || component?.name || product?.name || oi.name || 'Unknown Item';
        ordersMap.get(orderNum).items.push({ id: oi.id, name, type: oi.component_id ? 'Component' : 'Product', price: parseFloat(oi.unit_price) || 0, quantity: oi.quantity || 1, shipper: oi.shipper, itemStatus: oi.item_status, image: product?.thumbnail_path || '/assets/images/product-fallback.jpg' });
      });

      const ordersList = Array.from(ordersMap.values()).map(order => {
        const hasItems = order.items.length > 0;
        const allItemsDelivered = hasItems && order.items.every(i => (i.itemStatus || '').toLowerCase() === 'delivered');
        const anyItemDelivered = hasItems && order.items.some(i => (i.itemStatus || '').toLowerCase() === 'delivered');
        const allItemsShippedOrDelivered = hasItems && order.items.every(i => {
          const s = (i.itemStatus || '').toLowerCase();
          return s === 'shipped' || s === 'delivered';
        });

        // An order is completed for the seller if all items have been shipped/fulfilled or delivered
        const isOrderComplete = (order.status || '').toLowerCase() === 'completed' || 
                                (order.status || '').toLowerCase() === 'delivered' || 
                                allItemsDelivered || 
                                anyItemDelivered ||
                                allItemsShippedOrDelivered;

        const displayStatus = (allItemsDelivered || anyItemDelivered || (order.status || '').toLowerCase() === 'delivered')
          ? 'delivered'
          : (allItemsShippedOrDelivered ? 'shipped' : order.status);

        return {
          ...order,
          isComplete: isOrderComplete,
          status: displayStatus,
          total: order.items.reduce((sum, item) => sum + (item.price * item.quantity), 0)
        };
      });

      if (statusFilter === 'completed') {
        return ordersList.filter(o => o.isComplete).sort((a, b) => new Date(b.date) - new Date(a.date));
      } else if (statusFilter === 'pending') {
        return ordersList.filter(o => !o.isComplete).sort((a, b) => new Date(b.date) - new Date(a.date));
      }

      return ordersList.sort((a, b) => new Date(b.date) - new Date(a.date));
    } catch (err) {
      console.error('Error in getSellerOrders:', err);
      throw err;
    }
  },

  async getManufacturerOrders(userId, statusFilter = null) {
    try {
      if (!userId) throw new Error('User ID is required');
      
      const { data: mfgAccount } = await supabase.from('manufacturer_accounts').select('id').eq('user_id', userId).maybeSingle();
      if (!mfgAccount?.id) return [];

      const { data: mfgProfile } = await supabase.from('manufacturers').select('id').eq('manufacturer_account_id', mfgAccount.id).maybeSingle();
      if (!mfgProfile?.id) return [];

      let query = supabase
        .from('order_items')
        .select(`
          *,
          order:orders!inner (*),
          seller_components!inner (
            component_id,
            manufacturer_id,
            components (id, name, custom_name)
          )
        `)
        .eq('seller_components.manufacturer_id', mfgProfile.id);

      const { data: results, error } = await query.order('created_at', { foreignTable: 'order', ascending: false });
      if (error) throw error;

      const ordersMap = new Map();
      results.forEach(oi => {
        const orderNum = oi.order.order_number;
        if (!ordersMap.has(orderNum)) ordersMap.set(orderNum, { id: orderNum, orderId: oi.order_id, date: oi.order.created_at, status: oi.order.status, items: [] });
        const component = oi.seller_components?.components;
        const name = (component?.custom_name && component.custom_name.trim()) || component?.name || oi.name || 'Unknown Item';
        ordersMap.get(orderNum).items.push({ id: oi.id, name, type: 'Component', price: parseFloat(oi.unit_price) || 0, quantity: oi.quantity || 1, shipper: oi.shipper, itemStatus: oi.item_status, image: '/assets/images/product-fallback.jpg' });
      });

      const ordersList = Array.from(ordersMap.values()).map(order => {
        const hasItems = order.items.length > 0;
        const allItemsDelivered = hasItems && order.items.every(i => (i.itemStatus || '').toLowerCase() === 'delivered');
        const anyItemDelivered = hasItems && order.items.some(i => (i.itemStatus || '').toLowerCase() === 'delivered');
        const allItemsShippedOrDelivered = hasItems && order.items.every(i => {
          const s = (i.itemStatus || '').toLowerCase();
          return s === 'shipped' || s === 'delivered';
        });

        const isOrderComplete = (order.status || '').toLowerCase() === 'completed' || 
                                (order.status || '').toLowerCase() === 'delivered' || 
                                allItemsDelivered || 
                                anyItemDelivered ||
                                allItemsShippedOrDelivered;

        const displayStatus = (allItemsDelivered || anyItemDelivered || (order.status || '').toLowerCase() === 'delivered')
          ? 'delivered'
          : (allItemsShippedOrDelivered ? 'shipped' : order.status);

        return {
          ...order,
          isComplete: isOrderComplete,
          status: displayStatus,
          total: order.items.reduce((sum, item) => sum + (item.price * item.quantity), 0)
        };
      });

      if (statusFilter === 'completed') {
        return ordersList.filter(o => o.isComplete).sort((a, b) => new Date(b.date) - new Date(a.date));
      } else if (statusFilter === 'pending') {
        return ordersList.filter(o => !o.isComplete).sort((a, b) => new Date(b.date) - new Date(a.date));
      }

      return ordersList.sort((a, b) => new Date(b.date) - new Date(a.date));
    } catch (err) {
      console.error('Error in getManufacturerOrders:', err);
      throw err;
    }
  },

  async createShippingLabel(orderItemId, labelType = 'PAPER_85X11_TOP_HALF_LABEL') {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      const response = await fetch(`/api/v1/shipping/create-label`, { 
        method: 'POST', 
        headers: { 
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${session.access_token}` 
        }, 
        body: JSON.stringify({ 
          order_item_id: orderItemId,
          label_type: labelType 
        }) 
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Failed to create label');
      return data;
    } catch (err) {
      console.error('Error creating shipping label:', err);
      throw err;
    }
  },

  async confirmShippingLabel(orderItemId, shipperDetails = null) {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      
      const response = await fetch(`/api/v1/shipping/confirm-label`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ 
            order_item_id: orderItemId,
            shipper_details: shipperDetails
        })
      });
      
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Failed to confirm label');
      return data;
    } catch (err) {
      console.error('Error confirming shipping label:', err);
      throw err;
    }
  },

  async resetShippingLabel(orderItemId) {
    try {
      // Symmetrical Reset: Fetch current shipper first, then surgically clear only tracking_number and label_url
      // This perfectly preserves the original carrier, delivery method, and price selections!
      const { data: itemData, error: fetchError } = await supabase
        .from('order_items')
        .select('shipper')
        .eq('id', orderItemId)
        .single();

      if (fetchError) throw fetchError;

      const currentShipper = itemData?.shipper || {};
      const updatedShipper = {
        ...currentShipper,
        tracking_number: null,
        label_url: null,
        print_status: 'unprinted'
      };

      const { error } = await supabase
        .from('order_items')
        .update({
          shipper: updatedShipper,
          item_status: 'pending',
          manufacturing_status: 'Pending'
        })
        .eq('id', orderItemId);

      if (error) throw error;
      return { success: true };
    } catch (err) {
      console.error('Error resetting shipping label:', err);
      throw err;
    }
  },

  async verifyItemPrices(cartItems) {
    try {
      const productIds = [...new Set(cartItems.map(item => item.productId).filter(Boolean))];
      const componentIds = [...new Set(cartItems.map(item => item.componentId).filter(Boolean))];
      const pricePromises = [];
      if (productIds.length > 0) pricePromises.push(supabase.from('products').select('id, price').in('id', productIds));
      if (componentIds.length > 0) pricePromises.push(supabase.from('components').select('id, price').in('id', componentIds));
      const results = await Promise.all(pricePromises);
      const currentPrices = {};
      results.forEach(result => { if (result.data) result.data.forEach(item => { currentPrices[item.id] = item.price; }); });
      let priceChanged = false;
      let newTotal = 0;
      let oldTotal = 0;
      const changedItems = [];
      cartItems.forEach(item => {
        const itemId = item.componentId || item.productId;
        const currentPrice = currentPrices[itemId];
        const cartPrice = item.cartPrice;
        oldTotal += cartPrice * item.quantity;
        newTotal += (currentPrice !== undefined ? currentPrice : cartPrice) * item.quantity;
        if (currentPrice !== undefined && Number(currentPrice) !== Number(cartPrice)) {
          priceChanged = true;
          changedItems.push({ cartItemId: item.cartItemId, name: item.customName || item.name, oldPrice: cartPrice, newPrice: currentPrice, quantity: item.quantity });
        }
      });
      return { priceChanged, newTotal, oldTotal, changedItems };
    } catch (err) {
      console.error('Error verifying item prices:', err);
      throw new Error('Could not verify item prices.');
    }
  }
};